import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const GOLD = '#FFC220';

const BLOCK_TYPES = [
  { value: 'text_image', label: 'Text + Image' },
  { value: 'text_image_small', label: 'Text + Small Image' },
  { value: 'text_only', label: 'Text Only' },
  { value: 'stats', label: 'Stats Bar' },
  { value: 'cards', label: 'Cards Grid' },
  { value: 'hero_image', label: 'Full Width Image + Text' },
  { value: 'cta', label: 'Call to Action Banner' },
  { value: 'faq', label: 'FAQ Accordion' },
  { value: 'instagram_feed', label: 'Instagram Feed' },
  { value: 'testimonials', label: 'Testimonials Carousel' },
  { value: 'image_columns', label: 'Image Columns' },
  { value: 'bullet_columns', label: 'Bullet Points + Quotes' },
  { value: 'image_only', label: 'Image Only' },
];

const PAGES = [
  'home', 'signup', 'merchants', 'savings', 'featuredrestaurant',
  'coupons', 'faq', 'mealplans', 'addvalue', 'quickaddvalue',
  'usingfmp', 'autoreload', 'inpersonpickup', 'mailmycard',
  'successful-enrollment', 'aboutus', 'tellyourfriends', 'tellyourparents', 'goldcard',
  'grubhub',
];

const emptyForm = {
  page: 'home',
  block_type: 'text_image',
  sort_order: 1,
  eyebrow: '',
  headline: '',
  body: '',
  button_text: '',
  button_url: '',
  button2_text: '',
  button2_url: '',
  image_url: '',
  image_position: 'right',
  background_color: 'white',
  text_align: 'left',
  active: true,
  stats: [
    { number: '', label: '' },
    { number: '', label: '' },
    { number: '', label: '' },
    { number: '', label: '' },
  ],
  cards: [
    { icon: '', title: '', body: '' },
    { icon: '', title: '', body: '' },
    { icon: '', title: '', body: '' },
    { icon: '', title: '', body: '' },
  ],
  faq: [
    { question: '', answer: '' },
    { question: '', answer: '' },
  ],
};

export default function ContentBlocks() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => { fetchBlocks(); }, []);

  const fetchBlocks = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('content_blocks')
      .select('*')
      .order('page', { ascending: true })
      .order('sort_order', { ascending: true });
    if (!error) setBlocks(data);
    setLoading(false);
  };

  const handleEdit = (block) => {
    setForm({
      page: block.page || 'home',
      block_type: block.block_type || 'text_image',
      sort_order: block.sort_order || 1,
      eyebrow: block.eyebrow || '',
      headline: block.headline || '',
      body: block.body || '',
      button_text: block.button_text || '',
      button_url: block.button_url || '',
      button2_text: block.button2_text || '',
      button2_url: block.button2_url || '',
      image_url: block.image_url || '',
      image_position: block.image_position || 'right',
      background_color: block.background_color || 'white',
      text_align: block.text_align || 'left',
      active: block.active,
      stats: block.stats || emptyForm.stats,
      cards: block.cards || emptyForm.cards,
      faq: block.faq || emptyForm.faq,
    });
    setEditingId(block.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this content block?')) return;
    await supabase.from('content_blocks').delete().eq('id', id);
    fetchBlocks();
  };

  const handleToggle = async (block) => {
    await supabase.from('content_blocks').update({ active: !block.active }).eq('id', block.id);
    fetchBlocks();
  };

  const handleMoveUp = async (block, pageBlocks, index) => {
    if (index === 0) return;
    const above = pageBlocks[index - 1];
    await supabase.from('content_blocks').update({ sort_order: above.sort_order }).eq('id', block.id);
    await supabase.from('content_blocks').update({ sort_order: block.sort_order }).eq('id', above.id);
    fetchBlocks();
  };

  const handleMoveDown = async (block, pageBlocks, index) => {
    if (index === pageBlocks.length - 1) return;
    const below = pageBlocks[index + 1];
    await supabase.from('content_blocks').update({ sort_order: below.sort_order }).eq('id', block.id);
    await supabase.from('content_blocks').update({ sort_order: block.sort_order }).eq('id', below.id);
    fetchBlocks();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.headline && !['stats', 'faq', 'instagram_feed', 'testimonials', 'image_columns', 'bullet_columns', 'image_only'].includes(form.block_type)) {
      alert('Please enter a headline.');
      return;
    }
    setSaving(true);
    const payload = {
      page: form.page,
      block_type: form.block_type,
      sort_order: parseInt(form.sort_order) || 1,
      eyebrow: form.eyebrow || null,
      headline: form.headline || null,
      body: form.body || null,
      button_text: form.button_text || null,
      button_url: form.button_url || null,
      button2_text: form.button2_text || null,
      button2_url: form.button2_url || null,
      image_url: form.image_url || null,
      image_position: form.image_position,
      background_color: form.background_color,
      text_align: form.text_align,
      active: form.active,
      stats: form.block_type === 'stats' ? form.stats.filter(s => s.number || s.label) : null,
      cards: ['cards', 'image_columns', 'bullet_columns'].includes(form.block_type) ? form.cards.filter(c => c) : null,
      faq: form.block_type === 'faq' ? form.faq.filter(f => f.question) : null,
    };

    if (editingId) {
      const { error } = await supabase.from('content_blocks').update(payload).eq('id', editingId);
      if (error) { alert('Error: ' + error.message); setSaving(false); return; }
      setSuccessMsg('Block updated!');
    } else {
      const { error } = await supabase.from('content_blocks').insert(payload);
      if (error) { alert('Error: ' + error.message); setSaving(false); return; }
      setSuccessMsg('Block created!');
    }

    setTimeout(() => setSuccessMsg(''), 3000);
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
    setSaving(false);
    fetchBlocks();
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const updateStat = (index, field, value) => {
    const updated = [...form.stats];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, stats: updated });
  };

  const updateCard = (index, field, value) => {
    const updated = [...form.cards];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, cards: updated });
  };

  const updateFaq = (index, field, value) => {
    const updated = [...form.faq];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, faq: updated });
  };

  const addFaqItem = () => setForm({ ...form, faq: [...form.faq, { question: '', answer: '' }] });
  const removeFaqItem = (index) => setForm({ ...form, faq: form.faq.filter((_, i) => i !== index) });
  const addCard = () => setForm({ ...form, cards: [...form.cards, { icon: '', title: '', body: '' }] });
  const removeCard = (index) => setForm({ ...form, cards: form.cards.filter((_, i) => i !== index) });

  const pages = [...new Set(blocks.map(b => b.page))];
  const getBlockTypeLabel = (type) => BLOCK_TYPES.find(t => t.value === type)?.label || type;

  if (loading) return <div style={styles.loading}><p style={{ color: GOLD }}>Loading...</p></div>;

  return (
    <div style={styles.wrap}>

      {/* TOPBAR */}
      <div style={styles.topbar}>
        <h1 style={styles.topbarTitle}>
          Content Blocks
          <span style={styles.topbarCount}>{blocks.length} blocks</span>
        </h1>
        <button style={styles.topbarBtn} onClick={() => { setShowForm(!showForm); setEditingId(null); setForm(emptyForm); }}>
          {showForm && !editingId ? '✕ Cancel' : '+ Add Block'}
        </button>
      </div>

      <div style={styles.content}>

        {successMsg && <div style={styles.successBanner}>✅ {successMsg}</div>}

        {/* FORM */}
        {showForm && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>{editingId ? 'Edit Content Block' : 'Add New Content Block'}</h2>
            <form onSubmit={handleSubmit}>

              {/* Page + Block Type + Sort Order */}
              <div style={{ ...styles.formGrid, gridTemplateColumns: '1fr 1fr 1fr' }}>
                <div style={styles.formRow}>
                  <label style={styles.label}>PAGE *</label>
                  <select style={styles.select} value={form.page} onChange={(e) => setForm({ ...form, page: e.target.value })}>
                    {PAGES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                  </select>
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>BLOCK TYPE *</label>
                  <select style={styles.select} value={form.block_type} onChange={(e) => setForm({ ...form, block_type: e.target.value })}>
                    {BLOCK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div style={styles.formRow}>
                  <label style={styles.label}>SORT ORDER</label>
                  <input style={styles.input} type="number" min="1" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
                </div>
              </div>

              {/* Background + Text Align */}
              <div style={styles.formGrid}>
                <div style={styles.formRow}>
                  <label style={styles.label}>BACKGROUND</label>
                  <select style={styles.select} value={form.background_color} onChange={(e) => setForm({ ...form, background_color: e.target.value })}>
                    <option value="white">White</option>
                    <option value="gray">Light Gray</option>
                    <option value="dark">Dark</option>
                    <option value="gold">Gold</option>
                  </select>
                </div>
                {['text_only', 'cta'].includes(form.block_type) && (
                  <div style={styles.formRow}>
                    <label style={styles.label}>TEXT ALIGN</label>
                    <select style={styles.select} value={form.text_align} onChange={(e) => setForm({ ...form, text_align: e.target.value })}>
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                    </select>
                  </div>
                )}
              </div>

              {/* ── TEXT fields (shared) ── */}
              {['text_image', 'text_image_small', 'text_only', 'hero_image', 'cta', 'instagram_feed'].includes(form.block_type) && (
                <>
                  <div style={styles.formRow}>
                    <label style={styles.label}>EYEBROW TEXT</label>
                    <input style={styles.input} type="text" placeholder="e.g. WHAT IS FLATIRON MEAL PLAN?" value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} />
                  </div>
                  <div style={styles.formRow}>
                    <label style={styles.label}>HEADLINE *</label>
                    <input style={styles.input} type="text" placeholder="Main heading" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} required />
                  </div>
                  <div style={styles.formRow}>
                    <label style={styles.label}>BODY TEXT</label>
                    <textarea style={styles.textarea} rows={4} placeholder="Body text..." value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
                  </div>
                  <div style={styles.formGrid}>
                    <div style={styles.formRow}>
                      <label style={styles.label}>BUTTON 1 TEXT</label>
                      <input style={styles.input} type="text" placeholder="e.g. Sign up now" value={form.button_text} onChange={(e) => setForm({ ...form, button_text: e.target.value })} />
                    </div>
                    <div style={styles.formRow}>
                      <label style={styles.label}>BUTTON 1 URL</label>
                      <input style={styles.input} type="text" placeholder="https://..." value={form.button_url} onChange={(e) => setForm({ ...form, button_url: e.target.value })} />
                    </div>
                    <div style={styles.formRow}>
                      <label style={styles.label}>BUTTON 2 TEXT</label>
                      <input style={styles.input} type="text" placeholder="e.g. Learn more" value={form.button2_text} onChange={(e) => setForm({ ...form, button2_text: e.target.value })} />
                    </div>
                    <div style={styles.formRow}>
                      <label style={styles.label}>BUTTON 2 URL</label>
                      <input style={styles.input} type="text" placeholder="https://..." value={form.button2_url} onChange={(e) => setForm({ ...form, button2_url: e.target.value })} />
                    </div>
                  </div>
                </>
              )}

              {/* ── IMAGE fields ── */}
              {['text_image', 'text_image_small', 'hero_image'].includes(form.block_type) && (
                <div style={styles.formGrid}>
                  <div style={styles.formRow}>
                    <label style={styles.label}>IMAGE URL</label>
                    <input style={styles.input} type="text" placeholder="https://..." value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
                    {form.image_url && <img src={form.image_url} alt="Preview" style={styles.imagePreview} />}
                  </div>
                  {form.block_type === 'text_image' && (
                    <div style={styles.formRow}>
                      <label style={styles.label}>IMAGE POSITION</label>
                      <select style={styles.select} value={form.image_position} onChange={(e) => setForm({ ...form, image_position: e.target.value })}>
                        <option value="right">Text left, Image right</option>
                        <option value="left">Image left, Text right</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* ── STATS fields ── */}
              {form.block_type === 'stats' && (
                <>
                  <div style={styles.formRow}>
                    <label style={styles.label}>HEADLINE (optional)</label>
                    <input style={styles.input} type="text" placeholder="e.g. By the numbers" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} />
                  </div>
                  <div style={styles.sectionLabel}>STATS (up to 4)</div>
                  <div style={{ ...styles.formGrid, gridTemplateColumns: '1fr 1fr' }}>
                    {form.stats.map((stat, i) => (
                      <div key={i} style={styles.statRow}>
                        <div style={styles.statNum}>#{i + 1}</div>
                        <input style={{ ...styles.input, flex: 1 }} type="text" placeholder="Number e.g. 100+" value={stat.number} onChange={(e) => updateStat(i, 'number', e.target.value)} />
                        <input style={{ ...styles.input, flex: 1 }} type="text" placeholder="Label e.g. Merchants" value={stat.label} onChange={(e) => updateStat(i, 'label', e.target.value)} />
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* ── CARDS fields ── */}
              {form.block_type === 'cards' && (
                <>
                  <div style={styles.formRow}>
                    <label style={styles.label}>EYEBROW TEXT</label>
                    <input style={styles.input} type="text" placeholder="e.g. MANAGE YOUR ACCOUNT" value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} />
                  </div>
                  <div style={styles.formRow}>
                    <label style={styles.label}>HEADLINE *</label>
                    <input style={styles.input} type="text" placeholder="Section headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} />
                  </div>
                  <div style={{ ...styles.sectionLabel, marginBottom: '12px' }}>CARDS</div>
                  {form.cards.map((card, i) => (
                    <div key={i} style={styles.cardFormItem}>
                      <div style={styles.cardFormHeader}>
                        <span style={styles.cardFormNum}>Card {i + 1}</span>
                        {form.cards.length > 1 && <button type="button" style={styles.removeBtn} onClick={() => removeCard(i)}>Remove</button>}
                      </div>
                      <div style={styles.formGrid}>
                        <div style={styles.formRow}>
                          <label style={styles.label}>ICON (emoji)</label>
                          <input style={styles.input} type="text" placeholder="e.g. 🔑" value={card.icon} onChange={(e) => updateCard(i, 'icon', e.target.value)} />
                        </div>
                        <div style={styles.formRow}>
                          <label style={styles.label}>TITLE</label>
                          <input style={styles.input} type="text" placeholder="Card title" value={card.title} onChange={(e) => updateCard(i, 'title', e.target.value)} />
                        </div>
                        <div style={{ ...styles.formRow, gridColumn: '1 / -1' }}>
                          <label style={styles.label}>BODY</label>
                          <input style={styles.input} type="text" placeholder="Card description" value={card.body} onChange={(e) => updateCard(i, 'body', e.target.value)} />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button type="button" style={styles.addItemBtn} onClick={addCard}>+ Add Card</button>
                </>
              )}

              {/* ── FAQ fields ── */}
              {form.block_type === 'faq' && (
                <>
                  <div style={styles.formRow}>
                    <label style={styles.label}>EYEBROW TEXT</label>
                    <input style={styles.input} type="text" placeholder="e.g. FREQUENTLY ASKED QUESTIONS" value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} />
                  </div>
                  <div style={styles.formRow}>
                    <label style={styles.label}>HEADLINE</label>
                    <input style={styles.input} type="text" placeholder="e.g. Got questions? We have answers." value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} />
                  </div>
                  <div style={{ ...styles.sectionLabel, marginBottom: '12px' }}>FAQ ITEMS</div>
                  {form.faq.map((item, i) => (
                    <div key={i} style={styles.cardFormItem}>
                      <div style={styles.cardFormHeader}>
                        <span style={styles.cardFormNum}>Q{i + 1}</span>
                        {form.faq.length > 1 && <button type="button" style={styles.removeBtn} onClick={() => removeFaqItem(i)}>Remove</button>}
                      </div>
                      <div style={styles.formRow}>
                        <label style={styles.label}>QUESTION</label>
                        <input style={styles.input} type="text" placeholder="Question..." value={item.question} onChange={(e) => updateFaq(i, 'question', e.target.value)} />
                      </div>
                      <div style={styles.formRow}>
                        <label style={styles.label}>ANSWER</label>
                        <textarea style={styles.textarea} rows={3} placeholder="Answer..." value={item.answer} onChange={(e) => updateFaq(i, 'answer', e.target.value)} />
                      </div>
                    </div>
                  ))}
                  <button type="button" style={styles.addItemBtn} onClick={addFaqItem}>+ Add Question</button>
                </>
              )}

              {/* ── TESTIMONIALS fields ── */}
              {form.block_type === 'testimonials' && (
                <>
                  <div style={styles.formRow}>
                    <label style={styles.label}>EYEBROW TEXT</label>
                    <input style={styles.input} type="text" placeholder="e.g. WHAT PEOPLE ARE SAYING" value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} />
                  </div>
                  <div style={styles.formRow}>
                    <label style={styles.label}>HEADLINE</label>
                    <input style={styles.input} type="text" placeholder="e.g. Don't just take our word for it." value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} />
                  </div>
                </>
              )}

              {/* ── IMAGE COLUMNS fields ── */}
              {form.block_type === 'image_columns' && (
                <>
                  <div style={styles.formRow}>
                    <label style={styles.label}>EYEBROW TEXT</label>
                    <input style={styles.input} type="text" placeholder="e.g. WHY CHOOSE FMP?" value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} />
                  </div>
                  <div style={styles.formRow}>
                    <label style={styles.label}>SECTION HEADLINE</label>
                    <input style={styles.input} type="text" placeholder="e.g. Everything you need in one card" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} />
                  </div>
                  <div style={styles.formRow}>
                    <label style={styles.label}>NUMBER OF COLUMNS</label>
                    <select style={styles.select}
                      value={form.cards?.length === 3 ? '3' : '4'}
                      onChange={(e) => {
                        const count = parseInt(e.target.value);
                        const current = form.cards || [];
                        const empty = { image_url: '', title: '', body: '', button_text: '', button_url: '' };
                        if (count > current.length) {
                          setForm({ ...form, cards: [...current, ...Array(count - current.length).fill({ ...empty })] });
                        } else {
                          setForm({ ...form, cards: current.slice(0, count) });
                        }
                      }}>
                      <option value="3">3 Columns</option>
                      <option value="4">4 Columns</option>
                    </select>
                  </div>
                  <div style={{ ...styles.sectionLabel, marginBottom: '12px' }}>COLUMNS</div>
                  {(form.cards || [
                    { image_url: '', title: '', body: '', button_text: '', button_url: '' },
                    { image_url: '', title: '', body: '', button_text: '', button_url: '' },
                    { image_url: '', title: '', body: '', button_text: '', button_url: '' },
                  ]).map((col, i) => (
                    <div key={i} style={styles.cardFormItem}>
                      <div style={styles.cardFormHeader}>
                        <span style={styles.cardFormNum}>Column {i + 1}</span>
                      </div>
                      <div style={styles.formRow}>
                        <label style={styles.label}>IMAGE URL</label>
                        <input style={styles.input} type="text" placeholder="https://... (Supabase storage URL)" value={col.image_url || ''} onChange={(e) => updateCard(i, 'image_url', e.target.value)} />
                        {col.image_url && <img src={col.image_url} alt="Preview" style={{ ...styles.imagePreview, height: '80px', width: '80px', objectFit: 'cover' }} />}
                      </div>
                      <div style={styles.formGrid}>
                        <div style={styles.formRow}>
                          <label style={styles.label}>HEADLINE</label>
                          <input style={styles.input} type="text" placeholder="Column headline" value={col.title || ''} onChange={(e) => updateCard(i, 'title', e.target.value)} />
                        </div>
                        <div style={styles.formRow}>
                          <label style={styles.label}>BODY TEXT</label>
                          <input style={styles.input} type="text" placeholder="Short description" value={col.body || ''} onChange={(e) => updateCard(i, 'body', e.target.value)} />
                        </div>
                        <div style={styles.formRow}>
                          <label style={styles.label}>BUTTON TEXT (optional)</label>
                          <input style={styles.input} type="text" placeholder="e.g. Learn more" value={col.button_text || ''} onChange={(e) => updateCard(i, 'button_text', e.target.value)} />
                        </div>
                        <div style={styles.formRow}>
                          <label style={styles.label}>BUTTON URL (optional)</label>
                          <input style={styles.input} type="text" placeholder="https://..." value={col.button_url || ''} onChange={(e) => updateCard(i, 'button_url', e.target.value)} />
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* ── BULLET COLUMNS fields ── */}
              {form.block_type === 'bullet_columns' && (
                <>
                  <div style={styles.formRow}>
                    <label style={styles.label}>EYEBROW TEXT</label>
                    <input style={styles.input} type="text" placeholder="e.g. WHY CHOOSE FMP?" value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} />
                  </div>
                  <div style={styles.formRow}>
                    <label style={styles.label}>SECTION HEADLINE (optional)</label>
                    <input style={styles.input} type="text" placeholder="e.g. Why Students Love the Flatiron Meal Plan" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} />
                  </div>
                  <div style={styles.cardFormItem}>
                    <div style={styles.cardFormHeader}>
                      <span style={styles.cardFormNum}>Left Column</span>
                    </div>
                    <div style={styles.formRow}>
                      <label style={styles.label}>COLUMN HEADER</label>
                      <input style={styles.input} type="text" placeholder="e.g. Why Students Love FMP"
                        value={form.cards?.[0]?.title || ''}
                        onChange={(e) => {
                          const updated = form.cards?.length >= 1 ? [...form.cards] : [{}, {}];
                          updated[0] = { ...updated[0], title: e.target.value };
                          setForm({ ...form, cards: updated });
                        }} />
                    </div>
                    <div style={styles.formRow}>
                      <label style={styles.label}>BULLET POINTS</label>
                      <textarea style={{ ...styles.textarea, minHeight: '160px' }} rows={8}
                        placeholder={"One bullet point per line. Use HTML to bold:\n<strong>Savings:</strong> Enjoy cardholder coupons...\n<strong>Convenience:</strong> Shop for groceries..."}
                        value={form.cards?.[0]?.body || ''}
                        onChange={(e) => {
                          const updated = form.cards?.length >= 1 ? [...form.cards] : [{}, {}];
                          updated[0] = { ...updated[0], body: e.target.value };
                          setForm({ ...form, cards: updated });
                        }} />
                      <div style={styles.fieldHint}>One bullet per line. Use &lt;strong&gt;word&lt;/strong&gt; to bold text.</div>
                    </div>
                  </div>
                  <div style={styles.cardFormItem}>
                    <div style={styles.cardFormHeader}>
                      <span style={styles.cardFormNum}>Right Column</span>
                    </div>
                    <div style={styles.formRow}>
                      <label style={styles.label}>COLUMN HEADER</label>
                      <input style={styles.input} type="text" placeholder="e.g. Why Parents Love FMP"
                        value={form.cards?.[1]?.title || ''}
                        onChange={(e) => {
                          const updated = form.cards?.length >= 2 ? [...form.cards] : [{ ...(form.cards?.[0] || {}) }, {}];
                          updated[1] = { ...updated[1], title: e.target.value };
                          setForm({ ...form, cards: updated });
                        }} />
                    </div>
                    <div style={styles.formRow}>
                      <label style={styles.label}>BULLET POINTS</label>
                      <textarea style={{ ...styles.textarea, minHeight: '160px' }} rows={8}
                        placeholder={"One bullet point per line. Use HTML to bold:\n<strong>Security:</strong> Rest assured food money...\n<strong>Ease of Use:</strong> Manage and monitor..."}
                        value={form.cards?.[1]?.body || ''}
                        onChange={(e) => {
                          const updated = form.cards?.length >= 2 ? [...form.cards] : [{ ...(form.cards?.[0] || {}) }, {}];
                          updated[1] = { ...updated[1], body: e.target.value };
                          setForm({ ...form, cards: updated });
                        }} />
                      <div style={styles.fieldHint}>One bullet per line. Use &lt;strong&gt;word&lt;/strong&gt; to bold text.</div>
                    </div>
                  </div>
                </>
              )}

              {/* ── IMAGE ONLY fields ── */}
              {form.block_type === 'image_only' && (
                <>
                  <div style={styles.formRow}>
                    <label style={styles.label}>IMAGE URL *</label>
                    <input
                      style={styles.input}
                      type="text"
                      placeholder="https://... (Supabase storage URL)"
                      value={form.image_url}
                      onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    />
                    {form.image_url && (
                      <img src={form.image_url} alt="Preview" style={styles.imagePreview} />
                    )}
                  </div>
                  <div style={styles.formRow}>
                    <label style={styles.label}>CAPTION (optional)</label>
                    <input
                      style={styles.input}
                      type="text"
                      placeholder="Optional caption below the image"
                      value={form.body}
                      onChange={(e) => setForm({ ...form, body: e.target.value })}
                    />
                  </div>
                </>
              )}

              {/* Active toggle */}
              <div style={{ ...styles.formRow, marginTop: '16px' }}>
                <label style={styles.checkLabel}>
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} style={{ marginRight: '8px' }} />
                  Active — visible on website
                </label>
              </div>

              {/* Submit buttons — always at bottom */}
              <div style={styles.formBtns}>
                <button type="submit" style={styles.submitBtn} disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Block'}
                </button>
                <button type="button" style={styles.cancelBtn} onClick={handleCancel}>Cancel</button>
              </div>

            </form>
          </div>
        )}

        {/* BLOCKS LIST */}
        {pages.length === 0 ? (
          <div style={styles.empty}>No content blocks yet. Click "+ Add Block" to create one.</div>
        ) : (
          pages.map((page) => {
            const pageBlocks = blocks.filter(b => b.page === page);
            return (
              <div key={page} style={styles.pageSection}>
                <div style={styles.pageHeader}>
                  <h2 style={styles.pageTitle}>{page.charAt(0).toUpperCase() + page.slice(1)}</h2>
                  <span style={styles.pageCount}>{pageBlocks.length} blocks</span>
                </div>
                {pageBlocks.map((block, index) => (
                  <div key={block.id} style={{ ...styles.blockRow, ...(block.active ? {} : styles.blockRowInactive) }}>
                    <div style={styles.blockOrder}>{block.sort_order}</div>
                    <div style={styles.blockContent}>
                      <div style={styles.blockTypeBadge}>{getBlockTypeLabel(block.block_type)}</div>
                      {block.eyebrow && <div style={styles.blockEyebrow}>{block.eyebrow}</div>}
                      <div style={styles.blockHeadline}>{block.headline || '—'}</div>
                      {block.body && <div style={styles.blockBody}>{block.body.substring(0, 80)}...</div>}
                    </div>
                    <div style={styles.blockActions}>
                      <div style={styles.reorderBtns}>
                        <button style={styles.arrowBtn} onClick={() => handleMoveUp(block, pageBlocks, index)} disabled={index === 0}>↑</button>
                        <button style={styles.arrowBtn} onClick={() => handleMoveDown(block, pageBlocks, index)} disabled={index === pageBlocks.length - 1}>↓</button>
                      </div>
                      <Toggle value={block.active} onChange={() => handleToggle(block)} />
                      <button style={styles.editBtn} onClick={() => handleEdit(block)}>Edit</button>
                      <button style={styles.deleteBtn} onClick={() => handleDelete(block.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })
        )}

      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div onClick={onChange} style={{ width: '36px', height: '20px', borderRadius: '10px', background: value ? GOLD : '#ddd', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', width: '14px', height: '14px', borderRadius: '50%', background: '#fff', top: '3px', left: value ? '19px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
}

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' },
  topbar: { background: '#fff', borderBottom: '1px solid #e0e0e0', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 },
  topbarTitle: { fontSize: '18px', fontWeight: '500', color: '#111', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' },
  topbarCount: { fontSize: '13px', fontWeight: '400', color: '#aaa' },
  topbarBtn: { background: GOLD, color: '#111', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  content: { padding: '24px', flex: 1 },
  successBanner: { background: '#f0fff4', border: '1px solid #86efac', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', color: '#166534', fontSize: '14px' },
  formCard: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '14px', padding: '24px', marginBottom: '24px' },
  formTitle: { fontSize: '16px', fontWeight: '500', color: '#111', marginBottom: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '4px' },
  formRow: { marginBottom: '16px' },
  label: { display: 'block', color: '#888', fontSize: '10px', letterSpacing: '1px', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', color: '#111', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', color: '#111', fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', color: '#111', fontFamily: 'inherit', outline: 'none', background: '#fff', cursor: 'pointer', boxSizing: 'border-box' },
  imagePreview: { width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', marginTop: '8px', border: '1px solid #e0e0e0' },
  fieldHint: { color: '#bbb', fontSize: '11px', marginTop: '4px' },
  sectionLabel: { color: '#888', fontSize: '10px', letterSpacing: '1px', marginBottom: '8px' },
  statRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' },
  statNum: { width: '24px', height: '24px', borderRadius: '50%', background: '#111', color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', flexShrink: 0 },
  cardFormItem: { background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '16px', marginBottom: '12px' },
  cardFormHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  cardFormNum: { fontSize: '12px', fontWeight: '600', color: '#888' },
  removeBtn: { background: 'none', border: 'none', color: '#ff6b6b', fontSize: '12px', cursor: 'pointer' },
  addItemBtn: { background: 'none', border: '1px dashed #e0e0e0', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', color: '#888', cursor: 'pointer', width: '100%', marginBottom: '16px' },
  checkLabel: { display: 'flex', alignItems: 'center', fontSize: '14px', color: '#444', cursor: 'pointer' },
  formBtns: { display: 'flex', gap: '12px', marginTop: '8px' },
  submitBtn: { background: GOLD, color: '#111', border: 'none', borderRadius: '10px', padding: '11px 24px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  cancelBtn: { background: 'none', color: '#888', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '11px 24px', fontSize: '14px', cursor: 'pointer' },
  pageSection: { marginBottom: '28px' },
  pageHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' },
  pageTitle: { fontSize: '15px', fontWeight: '500', color: '#111', margin: 0 },
  pageCount: { fontSize: '12px', color: '#aaa' },
  blockRow: { display: 'flex', alignItems: 'center', gap: '14px', background: '#fff', border: '1px solid #e0e0e0', borderLeft: `4px solid ${GOLD}`, borderRadius: '0 12px 12px 0', padding: '14px 16px', marginBottom: '8px' },
  blockRowInactive: { opacity: 0.5, borderLeftColor: '#ddd' },
  blockOrder: { width: '28px', height: '28px', borderRadius: '50%', background: '#111', color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600', flexShrink: 0 },
  blockContent: { flex: 1, minWidth: 0 },
  blockTypeBadge: { display: 'inline-block', background: 'rgba(255,194,32,0.12)', color: '#b8860b', fontSize: '10px', fontWeight: '500', padding: '2px 8px', borderRadius: '20px', marginBottom: '4px' },
  blockEyebrow: { color: GOLD, fontSize: '9px', letterSpacing: '1.5px', fontWeight: '500', marginBottom: '3px' },
  blockHeadline: { fontSize: '14px', fontWeight: '500', color: '#111', marginBottom: '3px' },
  blockBody: { fontSize: '12px', color: '#888', lineHeight: 1.5 },
  blockActions: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
  reorderBtns: { display: 'flex', flexDirection: 'column', gap: '2px' },
  arrowBtn: { background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: '4px', width: '24px', height: '20px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, color: '#666' },
  editBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#444', cursor: 'pointer' },
  deleteBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', color: '#ff6b6b', cursor: 'pointer' },
  empty: { padding: '32px', textAlign: 'center', color: '#888', fontSize: '13px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '12px' },
};